import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Sparkles, Wallet, Upload, ChevronRight, ChevronLeft, ShieldCheck } from "lucide-react";

const STORAGE_KEY_PREFIX = "pm_la_tour_v1_";

/**
 * Three-step onboarding coachmark for newly-approved students on limited access.
 *
 * Shows on the dashboard only when:
 *   - The student's access status is `limited_access_active`
 *   - The per-user localStorage flag `pm_la_tour_v1_<uid>` is NOT set
 *
 * Skipping/finishing sets the flag so it never shows again for that user.
 */
export default function LimitedAccessTour() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [state, setState] = useState(null);

  useEffect(() => {
    if (!user?.id || user?.role !== "student") return;
    const dismissed = (() => {
      try { return localStorage.getItem(STORAGE_KEY_PREFIX + user.id); } catch { return null; }
    })();
    if (dismissed) return;

    // Only fetch access state if not already dismissed
    api.get("/student/access-status")
      .then((r) => r.data)
      .catch(() => null)
      .then((s) => {
      if (!s) return;
      setState(s);
      // Auto-open only for limited_access_active AND only when the student
      // has actually enrolled in a course. Brand-new students with no course
      // see the "No enrolled course yet" banner instead — the tour would be
      // confusing/misleading there.
      if (s.status === "limited_access_active" && s.has_enrolled_course) {
        // Small delay so the banner has rendered first
        setTimeout(() => setOpen(true), 600);
      }
    }).catch(() => {});
  }, [user?.id, user?.role]);

  const persistDismissal = () => {
    if (!user?.id) return;
    try { localStorage.setItem(STORAGE_KEY_PREFIX + user.id, String(Date.now())); } catch { /* noop */ }
  };

  const finish = () => {
    persistDismissal();
    setOpen(false);
  };

  if (!state || state.status !== "limited_access_active") return null;

  const steps = [
    {
      icon: Sparkles,
      accent: "text-indigo-600",
      ring: "bg-indigo-50 ring-indigo-200",
      title: `Welcome, ${user?.full_name?.split(" ")[0] || "student"}!`,
      kicker: "STEP 1 — WHAT YOU GET FREE",
      body: (
        <>
          You have <span className="font-semibold text-amber-700">limited access</span> to{" "}
          <span className="font-semibold">{state.course_name || "your course"}</span> for{" "}
          <span className="font-semibold">{state.days_remaining} day{state.days_remaining === 1 ? "" : "s"}</span>
          {state.question_limit > 0 && (
            <> and up to <span className="font-semibold">{state.question_limit} questions</span></>
          )}
          . Enough to explore the experience before deciding.
        </>
      ),
      extras: (
        <div className="flex flex-wrap gap-2 mt-3 text-xs">
          {Object.entries({
            review: "Review Mode",
            exam: "Exam Mode",
            smart_review: "Smart Review",
            ai_explanation: "AI Explanations",
            audio: "Audio Mode",
          }).map(([k, label]) => (
            <span key={k}
              className={`px-2 py-1 rounded-full border ${
                state.allows?.[k]
                  ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                  : "bg-slate-100 border-slate-200 text-slate-500 line-through"
              }`}>
              {label}
            </span>
          ))}
        </div>
      ),
    },
    {
      icon: Wallet,
      accent: "text-emerald-700",
      ring: "bg-emerald-50 ring-emerald-200",
      title: "Complete your payment to upgrade",
      kicker: "STEP 2 — HOW TO PAY",
      body: (
        <>
          Tap <span className="font-semibold">View Payment Methods</span> on your dashboard banner to see the payment
          options your administrator has enabled. Follow the instructions or scan the QR, complete the payment, and
          keep your reference number handy for the next step.
        </>
      ),
      extras: (
        <div className="mt-3 text-xs text-slate-600">
          Your dashboard banner shows the enabled payment methods.
        </div>
      ),
    },
    {
      icon: Upload,
      accent: "text-blue-700",
      ring: "bg-blue-50 ring-blue-200",
      title: "Upload your receipt to unlock everything",
      kicker: "STEP 3 — UNLOCK FULL ACCESS",
      body: (
        <>
          After paying, hit <span className="font-semibold">Upload Receipt</span> on the same banner — drop in a screenshot
          (JPG / PNG) or PDF up to 5 MB along with your payment reference number. Our admin verifies it personally,
          then your account flips to <span className="font-semibold text-emerald-700">Full Access</span> ✨
        </>
      ),
      extras: (
        <div className="mt-3 inline-flex items-center gap-1.5 text-xs text-emerald-700 font-semibold">
          <ShieldCheck className="w-4 h-4" /> You'll receive a push notification the moment it's confirmed.
        </div>
      ),
    },
  ];

  const current = steps[step];
  const Icon = current.icon;
  const isLast = step === steps.length - 1;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) finish(); }}>
      <DialogContent className="max-w-lg" data-testid="limited-access-tour">
        <DialogHeader>
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500" data-testid="tour-kicker">
            {current.kicker}
          </div>
          <DialogTitle className="flex items-center gap-3 mt-1">
            <span className={`h-10 w-10 rounded-xl grid place-items-center ring-4 ${current.ring} ${current.accent}`}>
              <Icon className="w-5 h-5" strokeWidth={1.75} />
            </span>
            <span data-testid="tour-title">{current.title}</span>
          </DialogTitle>
          <DialogDescription className="text-base text-slate-700 dark:text-slate-200 leading-relaxed pt-1" data-testid="tour-body">
            {current.body}
          </DialogDescription>
        </DialogHeader>

        {current.extras && <div data-testid="tour-extras">{current.extras}</div>}

        {/* progress dots */}
        <div className="flex items-center justify-center gap-2 pt-1" aria-label="Tour progress">
          {steps.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setStep(i)}
              aria-label={`Go to step ${i + 1}`}
              className={`h-2 rounded-full transition-all ${i === step ? "w-8 bg-blue-600" : "w-2 bg-slate-300 hover:bg-slate-400"}`}
              data-testid={`tour-dot-${i}`}
            />
          ))}
        </div>

        <DialogFooter className="gap-2 sm:justify-between flex-row">
          <Button
            variant="ghost"
            onClick={finish}
            className="text-slate-500 hover:text-slate-700 w-full sm:w-auto"
            data-testid="tour-skip-btn"
          >
            Skip tour
          </Button>
          <div className="flex gap-2 w-full sm:w-auto">
            {step > 0 && (
              <Button
                variant="outline"
                onClick={() => setStep((s) => Math.max(0, s - 1))}
                className="flex-1 sm:flex-none"
                data-testid="tour-back-btn"
              >
                <ChevronLeft className="w-4 h-4 mr-1" /> Back
              </Button>
            )}
            {isLast ? (
              <Button
                onClick={finish}
                className="bg-[#1E3A8A] hover:bg-[#1E40AF] flex-1 sm:flex-none"
                data-testid="tour-done-btn"
              >
                Got it!
              </Button>
            ) : (
              <Button
                onClick={() => setStep((s) => Math.min(steps.length - 1, s + 1))}
                className="bg-[#1E3A8A] hover:bg-[#1E40AF] flex-1 sm:flex-none"
                data-testid="tour-next-btn"
              >
                Next <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
